/**
 * TourSpotlight — dim overlay with a rounded cutout around a target element,
 * a light-gray arrow toward the cutout, and an anchored tooltip card.
 *
 * The arrow is drawn only for control-sized cutouts; see REGION_AREA_RATIO.
 *
 * The dialog node stays mounted across centered ↔ spotlight mode changes so
 * navigation handoffs never flash an empty guide. Spotlight mode waits for a
 * stable target rect before anchoring.
 *
 * Spotlight hit-testing: dimmed regions use four pointer-events-auto blockers;
 * the cutout hole is open so users can paste/Add in the Omnibar (and interact
 * with other spotlighted controls) while the tour card stays usable.
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export type TourPlacement = 'auto' | 'bottom' | 'top' | 'left' | 'right';

interface Rect {
    top: number;
    left: number;
    width: number;
    height: number;
    bottom: number;
    right: number;
}

interface TourSpotlightProps {
    /** CSS selector for the element to highlight. Null = centered modal. */
    targetSelector: string | null;
    /** Preferred placement of the tooltip relative to the cutout. */
    placement?: TourPlacement;
    /** Extra padding around the target cutout (px). */
    padding?: number;
    /** Tooltip / dialog content. */
    children: React.ReactNode;
    /** Optional — prefer no-op; tour should only dismiss via Skip/X. */
    onBackdropClick?: () => void;
    /**
     * When this value changes, the user's drag offset is cleared so a new tour
     * step re-anchors beside its target. Show-me focus changes should NOT bump
     * this — dragging once while exploring bullets should stick.
     */
    positionKey?: string | number;
}

const DRAG_HANDLE = '[data-tour-drag-handle]';

const ARROW_STROKE = '#a1a1aa'; // zinc-400
const ARROW_STROKE_SOFT = '#d4d4d8'; // zinc-300
const CARD_W = 420;
const CARD_GAP = 16;
const VIEW_PAD = 16;
/** Require this many consecutive successful measures before anchoring. */
const STABLE_HITS = 2;

/**
 * Above this share of the viewport, a cutout is a *region* (the graph canvas, a
 * docked panel) rather than a control, and the arrow is dropped.
 *
 * The arrow's only job is to answer "which control?". A region cutout has
 * already answered it, and `ArrowSvg`'s geometry actively misleads at that size:
 * it aims at the hole's centre, then pulls the tip back by half the hole's
 * smaller dimension. For the ~1000x700 canvas that is a ~356px pull away from a
 * point that was arbitrary to begin with, so the tip lands in empty space — on
 * the Graph Canvas step it appeared to point at the bottom of the map, nowhere
 * near the wallet the reader was looking at. A button's hole pulls ~15px and
 * lands just outside its edge, which is why small targets keep the arrow.
 */
const REGION_AREA_RATIO = 0.25;

function isRegionSized(hole: Rect): boolean {
    const viewport = window.innerWidth * window.innerHeight;
    if (viewport <= 0) return false;
    return (hole.width * hole.height) / viewport >= REGION_AREA_RATIO;
}

function measure(selector: string, padding: number): Rect | null {
    const el = document.querySelector(selector);
    if (!el) return null;
    const r = el.getBoundingClientRect();
    if (r.width < 2 && r.height < 2) return null;
    // Reject targets that sit entirely off-screen (e.g. Analysis accordion
    // headers below a 400px expanded sibling). Pointing the arrow there looks
    // like empty space — caller should scrollIntoView then remeasure.
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    if (r.bottom < 8 || r.top > vh - 8 || r.right < 8 || r.left > vw - 8) {
        return null;
    }
    return {
        top: r.top - padding,
        left: r.left - padding,
        width: r.width + padding * 2,
        height: r.height + padding * 2,
        bottom: r.bottom + padding,
        right: r.right + padding,
    };
}

function scrollTargetIntoView(selector: string): void {
    try {
        document.querySelector(selector)?.scrollIntoView({
            block: 'nearest',
            inline: 'nearest',
            behavior: 'auto',
        });
    } catch {
        // Malformed selector — measure() will also fail.
    }
}

function pickPlacement(
    preferred: TourPlacement,
    hole: Rect,
    cardH: number,
): Exclude<TourPlacement, 'auto'> {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const space = {
        bottom: vh - hole.bottom - VIEW_PAD,
        top: hole.top - VIEW_PAD,
        right: vw - hole.right - VIEW_PAD,
        left: hole.left - VIEW_PAD,
    };
    if (preferred !== 'auto') {
        if (space[preferred] >= cardH + CARD_GAP) return preferred;
    }
    const order: Exclude<TourPlacement, 'auto'>[] = ['bottom', 'top', 'right', 'left'];
    let best: Exclude<TourPlacement, 'auto'> = 'bottom';
    let bestSpace = -1;
    for (const side of order) {
        if (space[side] > bestSpace) {
            bestSpace = space[side];
            best = side;
        }
    }
    return best;
}

function cardPosition(
    side: Exclude<TourPlacement, 'auto'>,
    hole: Rect,
    cardH: number,
): { top: number; left: number } {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let top = 0;
    let left = 0;

    if (side === 'bottom') {
        top = hole.bottom + CARD_GAP;
        left = hole.left + hole.width / 2 - CARD_W / 2;
    } else if (side === 'top') {
        top = hole.top - CARD_GAP - cardH;
        left = hole.left + hole.width / 2 - CARD_W / 2;
    } else if (side === 'right') {
        top = hole.top + hole.height / 2 - cardH / 2;
        left = hole.right + CARD_GAP;
    } else {
        top = hole.top + hole.height / 2 - cardH / 2;
        left = hole.left - CARD_GAP - CARD_W;
    }

    left = Math.max(VIEW_PAD, Math.min(left, vw - CARD_W - VIEW_PAD));
    const maxTop = Math.max(VIEW_PAD, vh - cardH - VIEW_PAD);
    top = Math.max(VIEW_PAD, Math.min(top, maxTop));
    return { top, left };
}

function ArrowSvg({
    hole,
    card,
    side,
}: {
    hole: Rect;
    card: { top: number; left: number; width: number; height: number };
    side: Exclude<TourPlacement, 'auto'>;
}) {
    const hx = hole.left + hole.width / 2;
    const hy = hole.top + hole.height / 2;
    let sx = card.left + card.width / 2;
    let sy = card.top + card.height / 2;

    if (side === 'bottom') {
        sy = card.top;
        sx = Math.min(Math.max(card.left + 40, hx), card.left + card.width - 40);
    } else if (side === 'top') {
        sy = card.top + card.height;
        sx = Math.min(Math.max(card.left + 40, hx), card.left + card.width - 40);
    } else if (side === 'right') {
        sx = card.left;
        sy = Math.min(Math.max(card.top + 24, hy), card.top + card.height - 24);
    } else {
        sx = card.left + card.width;
        sy = Math.min(Math.max(card.top + 24, hy), card.top + card.height - 24);
    }

    const dx = hx - sx;
    const dy = hy - sy;
    const dist = Math.hypot(dx, dy) || 1;
    const tipPull = Math.min(hole.width, hole.height) / 2 + 6;
    const ex = hx - (dx / dist) * tipPull;
    const ey = hy - (dy / dist) * tipPull;

    const mx = (sx + ex) / 2;
    const my = (sy + ey) / 2;
    const nx = -dy / dist;
    const ny = dx / dist;
    const bend = Math.min(36, dist * 0.18);
    const cx = mx + nx * bend;
    const cy = my + ny * bend;

    const angle = Math.atan2(ey - cy, ex - cx);
    const headLen = 10;
    const a1 = angle + Math.PI * 0.82;
    const a2 = angle - Math.PI * 0.82;

    return (
        <svg
            className="pointer-events-none fixed inset-0 z-[202] w-full h-full"
            aria-hidden="true"
        >
            <path
                d={`M ${sx} ${sy} Q ${cx} ${cy} ${ex} ${ey}`}
                fill="none"
                stroke={ARROW_STROKE_SOFT}
                strokeWidth={2.5}
                strokeLinecap="round"
                opacity={0.55}
            />
            <path
                d={`M ${sx} ${sy} Q ${cx} ${cy} ${ex} ${ey}`}
                fill="none"
                stroke={ARROW_STROKE}
                strokeWidth={1.25}
                strokeLinecap="round"
                opacity={0.9}
            />
            <path
                d={`M ${ex} ${ey} L ${ex + Math.cos(a1) * headLen} ${ey + Math.sin(a1) * headLen} L ${ex + Math.cos(a2) * headLen} ${ey + Math.sin(a2) * headLen} Z`}
                fill={ARROW_STROKE}
                opacity={0.75}
            />
        </svg>
    );
}

export const TourSpotlight: React.FC<TourSpotlightProps> = ({
    targetSelector,
    placement = 'auto',
    padding = 10,
    children,
    onBackdropClick,
    positionKey,
}) => {
    const [hole, setHole] = useState<Rect | null>(null);
    const [stable, setStable] = useState(false);
    const [cardH, setCardH] = useState(280);
    /** User drag delta on top of auto-anchor / centered layout. */
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const cardRef = useRef<HTMLDivElement>(null);
    const hitsRef = useRef(0);
    const dragging = useRef(false);
    const dragPointer = useRef({ startX: 0, startY: 0, originX: 0, originY: 0 });

    // New tour step → re-anchor; Show-me focus must not clear this.
    useEffect(() => {
        setDragOffset({ x: 0, y: 0 });
    }, [positionKey]);

    const remeasure = useCallback(() => {
        if (!targetSelector) {
            hitsRef.current = 0;
            setHole(null);
            setStable(false);
            return;
        }
        const next = measure(targetSelector, padding);
        if (!next) {
            hitsRef.current = 0;
            setHole(null);
            setStable(false);
            return;
        }
        hitsRef.current += 1;
        setHole(next);
        if (hitsRef.current >= STABLE_HITS) {
            setStable(true);
        }
    }, [targetSelector, padding]);

    // Reset stability when the selector changes; scroll + double-measure so a
    // present target becomes stable in the same layout pass (no empty handoff
    // gap). Delayed remasures cover ensureUiReady mounts (Analysis accordion).
    useLayoutEffect(() => {
        hitsRef.current = 0;
        setStable(false);
        setHole(null);
        if (targetSelector) scrollTargetIntoView(targetSelector);
        remeasure();
        remeasure();
        const timers = [50, 100, 150, 300, 500, 800, 1500, 2500].map((ms) =>
            window.setTimeout(() => {
                if (targetSelector) scrollTargetIntoView(targetSelector);
                remeasure();
            }, ms),
        );
        return () => timers.forEach(clearTimeout);
    }, [remeasure, targetSelector]);

    useEffect(() => {
        const onScrollOrResize = () => remeasure();
        window.addEventListener('resize', onScrollOrResize);
        window.addEventListener('scroll', onScrollOrResize, true);
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(remeasure) : null;
        if (targetSelector) {
            const el = document.querySelector(targetSelector);
            if (el && ro) ro.observe(el);
        }
        return () => {
            window.removeEventListener('resize', onScrollOrResize);
            window.removeEventListener('scroll', onScrollOrResize, true);
            ro?.disconnect();
        };
    }, [remeasure, targetSelector]);

    useLayoutEffect(() => {
        if (cardRef.current) {
            setCardH(cardRef.current.getBoundingClientRect().height);
        }
    }, [children, hole, stable, dragOffset]);

    const clampOffset = useCallback((baseTop: number, baseLeft: number, dx: number, dy: number) => {
        const el = cardRef.current;
        const w = el?.offsetWidth ?? CARD_W;
        const h = el?.offsetHeight ?? cardH;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const minDx = VIEW_PAD - baseLeft;
        const maxDx = vw - VIEW_PAD - w - baseLeft;
        const minDy = VIEW_PAD - baseTop;
        const maxDy = vh - VIEW_PAD - h - baseTop;
        return {
            x: Math.max(minDx, Math.min(dx, maxDx)),
            y: Math.max(minDy, Math.min(dy, maxDy)),
        };
    }, [cardH]);

    const onDragMove = useCallback((e: PointerEvent) => {
        if (!dragging.current) return;
        const { startX, startY, originX, originY } = dragPointer.current;
        const baseTop = Number(cardRef.current?.dataset.baseTop ?? 0);
        const baseLeft = Number(cardRef.current?.dataset.baseLeft ?? 0);
        const next = clampOffset(
            baseTop,
            baseLeft,
            originX + (e.clientX - startX),
            originY + (e.clientY - startY),
        );
        setDragOffset(next);
    }, [clampOffset]);

    const onDragEnd = useCallback((e: PointerEvent) => {
        if (!dragging.current) return;
        dragging.current = false;
        document.body.style.userSelect = '';
        window.removeEventListener('pointermove', onDragMove);
        window.removeEventListener('pointerup', onDragEnd);
        window.removeEventListener('pointercancel', onDragEnd);
        try {
            (e.target as HTMLElement | null)?.releasePointerCapture?.(e.pointerId);
        } catch {
            // Already released.
        }
    }, [onDragMove]);

    const onCardPointerDown = useCallback((e: React.PointerEvent) => {
        if (e.button !== 0) return;
        const handle = (e.target as HTMLElement | null)?.closest?.(DRAG_HANDLE);
        if (!handle || !cardRef.current?.contains(handle)) return;
        e.preventDefault();
        dragging.current = true;
        dragPointer.current = {
            startX: e.clientX,
            startY: e.clientY,
            originX: dragOffset.x,
            originY: dragOffset.y,
        };
        document.body.style.userSelect = 'none';
        (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
        window.addEventListener('pointermove', onDragMove);
        window.addEventListener('pointerup', onDragEnd);
        window.addEventListener('pointercancel', onDragEnd);
    }, [dragOffset.x, dragOffset.y, onDragMove, onDragEnd]);

    useEffect(() => () => {
        window.removeEventListener('pointermove', onDragMove);
        window.removeEventListener('pointerup', onDragEnd);
        window.removeEventListener('pointercancel', onDragEnd);
        document.body.style.userSelect = '';
    }, [onDragMove, onDragEnd]);

    const useSpotlight = Boolean(targetSelector && hole && stable);
    const side = hole ? pickPlacement(placement, hole, cardH) : 'bottom';
    const anchored = useSpotlight && hole ? cardPosition(side, hole, cardH) : null;

    // Centered mode: start mid-viewport then apply drag. Spotlight: auto-anchor + drag.
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
    const baseTop = anchored
        ? anchored.top
        : Math.max(VIEW_PAD, vh / 2 - cardH / 2);
    const baseLeft = anchored
        ? anchored.left
        : Math.max(VIEW_PAD, vw / 2 - CARD_W / 2);
    const placed = clampOffset(baseTop, baseLeft, dragOffset.x, dragOffset.y);
    const finalTop = baseTop + placed.y;
    const finalLeft = baseLeft + placed.x;

    const cardShell =
        'w-[calc(100%-2rem)] max-h-[min(80vh,calc(100vh-2rem))] overflow-hidden flex flex-col rounded-xl border border-border bg-card shadow-2xl relative pointer-events-auto';

    return createPortal(
        <>
            {/* Backdrop — click does not dismiss unless caller opts in.
                Spotlight mode: dimmed strips block clicks; the cutout hole
                has no hit-target so the real Omnibar/canvas stays usable. */}
            <div
                className={
                    useSpotlight && hole
                        ? 'fixed inset-0 z-[200] pointer-events-none'
                        : 'fixed inset-0 z-[200] pointer-events-auto bg-black/60 backdrop-blur-[1px]'
                }
                onClick={onBackdropClick}
                aria-hidden="true"
                data-testid="tour-backdrop"
            >
                {useSpotlight && hole && (
                    <>
                        <div
                            className="absolute rounded-xl transition-[top,left,width,height] duration-200 ease-out pointer-events-none"
                            data-testid="tour-cutout"
                            style={{
                                top: hole.top,
                                left: hole.left,
                                width: hole.width,
                                height: hole.height,
                                boxShadow: '0 0 0 9999px rgba(0,0,0,0.55)',
                                outline: '1.5px solid rgba(212, 212, 216, 0.55)',
                            }}
                        />
                        {/* Four blockers around the hole — leave the cutout open */}
                        <div
                            className="absolute left-0 right-0 top-0 pointer-events-auto"
                            data-testid="tour-blocker-top"
                            style={{ height: Math.max(0, hole.top) }}
                            onClick={onBackdropClick}
                        />
                        <div
                            className="absolute left-0 right-0 bottom-0 pointer-events-auto"
                            data-testid="tour-blocker-bottom"
                            style={{ top: hole.bottom }}
                            onClick={onBackdropClick}
                        />
                        <div
                            className="absolute left-0 pointer-events-auto"
                            data-testid="tour-blocker-left"
                            style={{
                                top: hole.top,
                                width: Math.max(0, hole.left),
                                height: hole.height,
                            }}
                            onClick={onBackdropClick}
                        />
                        <div
                            className="absolute right-0 pointer-events-auto"
                            data-testid="tour-blocker-right"
                            style={{
                                top: hole.top,
                                left: hole.right,
                                height: hole.height,
                            }}
                            onClick={onBackdropClick}
                        />
                    </>
                )}
            </div>

            {useSpotlight && hole && !isRegionSized(hole) && (
                <ArrowSvg
                    hole={hole}
                    card={{
                        top: finalTop,
                        left: finalLeft,
                        width: CARD_W,
                        height: cardH,
                    }}
                    side={side}
                />
            )}

            {/* Single wrapper + single dialog — never unmount children on mode switch.
                Always absolute so free-drag works in centered mode too. */}
            <div
                className="fixed inset-0 z-[203] pointer-events-none"
                data-testid="tour-card-shell"
            >
                <div
                    ref={cardRef}
                    className={`absolute max-w-[420px] ${cardShell}`}
                    style={{ top: finalTop, left: finalLeft }}
                    data-base-top={baseTop}
                    data-base-left={baseLeft}
                    role="dialog"
                    aria-modal="true"
                    data-tour-mode={useSpotlight ? 'spotlight' : 'centered'}
                    data-testid="tour-card"
                    onClick={(e) => e.stopPropagation()}
                    onPointerDown={onCardPointerDown}
                >
                    {children}
                </div>
            </div>
        </>,
        document.body,
    );
};
